<template>
  <div
    class="form-component w-full flex flex-col items-start gap-0.5"
    :class="{ 'with-error': state.isDirty && !state.isValid }">
    <label
      v-if="props.label"
      :for="props.id"
      class="w-full !text-sm !text-gray !font-normal"
      :class="{ '!text-red': state.isDirty && !state.isValid }">
      {{ props.label }}
    </label>

    <input
      type="text"
      :id="props.id"
      :placeholder="props.placeholder"
      class="w-full h-8 border border-black text-sm text-gray py-2 px-4 rounded-full bg-transparent"
      :class="{ '!border-red': state.isDirty && !state.isValid, 'pointer-events-none': props.isReadOnly }"
      :readonly="props.isReadOnly"
      v-model="state.value"
      @input="onInput"
      @change="emitChange" />

    <div
      v-if="state.isDirty && !state.isValid"
      class="text-sm text-red">
      <span
        v-for="error in state.errors"
        :key="error"
        >{{ error }}</span
      >
    </div>
  </div>
</template>

<script setup lang="ts">
// @ts-nocheck

import { reactive, watch, onMounted } from 'vue';

const props = defineProps({
  label: String,
  id: String,
  name: String,
  placeholder: String,
  value: String,
  isRequired: Boolean,
  isReadOnly: Boolean,
  minLength: Number,
  maxLength: Number,
  triggerValidation: Number,
  triggerResetValidation: Number,
  serverError: String,
});

const emit = defineEmits(['update:value', 'check-is-valid', 'on-change']);

const state = reactive({
  value: props.value ?? '',
  isDirty: false,
  isValid: false,
  errors: [] as string[],
});

const validate = () => {
  state.isDirty = true;
  state.errors = [];

  if (props.serverError?.length) {
    state.errors.push(props.serverError);
  }

  if (props.isRequired && !state.value.trim()) {
    state.errors.push('This field is required. Please fill it in.');
  }

  if (props.minLength && state.value.length < props.minLength) {
    state.errors.push(`Minimum length is ${props.minLength} characters.`);
  }

  if (props.maxLength && state.value.length > props.maxLength) {
    state.errors.push(`Maximum length is ${props.maxLength} characters.`);
  }

  state.isValid = state.errors.length === 0;
  emit('check-is-valid', { name: props.name, isValid: state.isValid });
};

const reset = () => {
  state.isDirty = false;
  state.errors = [];
  state.isValid = false;
  emit('check-is-valid', { name: props.name, isValid: state.isValid });
};

const onInput = () => {
  validate();
  emit('update:value', state.value);
  emit('on-change');
};

const emitChange = () => {
  emit('on-change');
};

watch(
  () => props.value,
  (newVal) => {
    if (newVal !== state.value) {
      state.value = newVal ?? '';
      validate();
    }
  }
);

watch(() => props.triggerValidation, validate);
watch(() => props.triggerResetValidation, reset);

watch(
  () => props.serverError,
  validate
);

onMounted(() => {
  if (props.value?.length > 0) {
    state.value = props.value;
  }
});
</script>
